import { setIsOpenEditPopover } from "@/core/redux/slices/popover";
import { setActiveSpecificationItem } from "@/core/redux/slices/specification";
import { useDispatch, useSelector } from "@/core/redux/store";
import {
  ISpecificationCategoryResponse,
  ISpecificationItemResponse,
} from "@/shared/services/interface/responses/ISpecificationResponse";
import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useDrag } from "react-dnd";
import { CircleChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTreeDnD } from "../../hooks/tree-dnd-provider/useTreeDnD";
import { DND_TYPE } from "../../hooks/tree-dnd-provider/TreeDnDProvider";

export const CategoryItem = ({
  subItem,
  sidebarWidth,
  priority,
  subIndex,
  item,
  prevItemId,
  nextItemId,
}: {
  subItem: ISpecificationItemResponse;
  sidebarWidth: number;
  priority: string;
  subIndex: number;
  item: ISpecificationCategoryResponse;
  prevItemId: string | null;
  nextItemId: string | null;
}) => {
  const specification = useSelector((state) => state.specification);
  const activeSpecificationStep = specification.activeSpecificationStep;
  const dispatch = useDispatch();
  const { getPreviewClass } = useTreeDnD();

  // Back to local state - simpler and more performant
  const [expanded, setExpanded] = useState(false);
  const [showExpandButton, setShowExpandButton] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);

  // Description'ı memoize et
  const currentDescription = useMemo(() => {
    return activeSpecificationStep.id === subItem.id
      ? activeSpecificationStep.description
      : subItem.description;
  }, [
    activeSpecificationStep.id,
    activeSpecificationStep.description,
    subItem.id,
    subItem.description,
  ]);

  const isActive = activeSpecificationStep.id === subItem.id;

  useEffect(() => {
    if (isActive && itemRef.current) {
      const element = itemRef.current;
      const rect = element.getBoundingClientRect();
      const container =
        element.closest('[data-sidebar="sidebar"]') ||
        element.closest(".sidebar-content");

      if (container) {
        const containerRect = container.getBoundingClientRect();
        const isVisible =
          rect.top >= containerRect.top && rect.bottom <= containerRect.bottom;

        if (!isVisible) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  }, [isActive]);

  // Overflow kontrolü için stable dependency
  useEffect(() => {
    if (textRef.current) {
      const element = textRef.current;
      const originalWhiteSpace = element.style.whiteSpace;
      element.style.whiteSpace = "nowrap";
      const isOverflowing = element.scrollWidth > element.clientWidth;
      element.style.whiteSpace = originalWhiteSpace;

      setShowExpandButton(isOverflowing || expanded);
    }
  }, [currentDescription, expanded]);

  // Minimal drag - only to start drag
  const [{ isDragging }, dragRef] = useDrag(() => ({
    type: DND_TYPE,
    item: {
      id: subItem.id,
      nodeType: "ITEM" as const,
      parentId: item.id,
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  // FIXED: Include parent category when clicking an item
  const editItem = useCallback(
    (specItem: ISpecificationItemResponse, parentCategory: ISpecificationCategoryResponse) => {
      dispatch(setActiveSpecificationItem({
        ...specItem,
        category: {
          id: parentCategory.id,
          name: parentCategory.name,
        },
      }));
      dispatch(setIsOpenEditPopover(true));
    },
    [dispatch]
  );

  const handleExpandToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);

  // Get preview class
  const previewClass = getPreviewClass(subItem.id);

  return (
    <div
      ref={(el) => {
        (itemRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        dragRef(el);
      }}
      data-node-id={subItem.id}
      data-node-type="ITEM"
      data-node-parent-id={item.id}
      data-node-prev-item-id={prevItemId}
      data-node-next-item-id={nextItemId}
      data-sidebar="menu-sub-button"
      data-active={isActive}
      className={cn(
        "relative flex items-center text-muted-foreground border last:mb-[4px] gap-2 min-h-[45px] rounded-md px-2 py-1 cursor-pointer transition-all duration-100",
        !isActive &&
        "hover:bg-sidebar-accent border-transparent hover:text-sidebar-accent-foreground ",
        isActive && "bg-white  border-zinc-200",
        isDragging && "opacity-50"
      )}
      onMouseDown={() => {
        editItem({
          ...subItem,
          orderNo: `${priority}.${subIndex + 1}`
        }, item);
      }}
    >
      {previewClass === "dnd-insert-before" && (
        <div className="absolute top-[-2px] left-0 w-full h-[3px] opacity-40 bg-green-600">
          <div className="absolute size-[10px] bg-green-600 rounded-full top-[-4px] left-[-5px]"></div>
        </div>
      )}
      {previewClass === "dnd-insert-after" && (
        <div className="absolute bottom-[-3px] left-0 w-full h-[3px] opacity-40 bg-green-600">
          <div className="absolute size-[10px] bg-green-600 rounded-full top-[-4px] left-[-5px]"></div>
        </div>
      )}
      <span
        ref={textRef}
        className={cn(
          "text-xxs  font-500 flex-1 transition-all duration-200",
          expanded
            ? "whitespace-normal break-words"
            : "whitespace-nowrap overflow-hidden text-ellipsis"
        )}
      >
        {priority}.{subIndex + 1}. {currentDescription}
      </span>

      {showExpandButton && (
        <CircleChevronDown
          size={12}
          className={cn(
            "cursor-pointer text-muted-foreground flex-shrink-0 transition-transform duration-200",
            expanded && "rotate-180"
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleExpandToggle(e);
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}
    </div>
  );
};

export default CategoryItem;
